import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { apiError } from "../../lib/api";
import RichTextEditor from "../../components/admin/RichTextEditor";
import ImageUpload from "../../components/admin/ImageUpload";
import { ReadyDrafts, markDraftUsed } from "../../components/admin/ReadyDrafts";
import { platformIcon, PLATFORM_ACCENTS } from "../../lib/platforms";
import { toast } from "sonner";
import {
    Send,
    CheckCircle2,
    AlertCircle,
    Clock,
    ExternalLink,
    Sparkles,
    Eye,
    SpellCheck,
    Pencil,
    RotateCcw,
    Loader2,
    X,
} from "lucide-react";

const empty = {
    title: "",
    date: new Date().toISOString().slice(0, 10),
    cover_image_url: "",
    excerpt: "",
    body_html: "",
    caption: "",
    published: true,
};

export default function Compose() {
    const location = useLocation();
    const [channels, setChannels] = useState([]);
    const [selected, setSelected] = useState(["website"]);
    const [form, setForm] = useState(empty);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [checking, setChecking] = useState(false);

    const allUsable = (chs) => chs.filter((c) => c.status === "ready" || c.connected).map((c) => c.key);

    const load = useCallback(
        () =>
            api.get("/admin/social/channels").then((r) => {
                setChannels(r.data);
                setSelected(allUsable(r.data));
            }),
        []
    );
    useEffect(() => {
        load();
    }, [load]);

    // Prefill from AI Blog Writer "Send to blog editor"
    useEffect(() => {
        const d = location.state?.aiDraft;
        if (d) {
            setForm((f) => ({ ...f, title: d.title || "", body_html: d.body_html || "" }));
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const toggle = (key) =>
        setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

    const set = (k) => (e) =>
        setForm({ ...form, [k]: e && e.target ? e.target.value : e });

    const doPublish = async () => {
        setError("");
        if (!selected.length) {
            setError("Pick at least one place to publish.");
            setShowPreview(false);
            return;
        }
        setBusy(true);
        setResults(null);
        try {
            const { data } = await api.post("/admin/social/publish", {
                ...form,
                channels: selected,
            });
            setResults(data.broadcast.results);
            if (form.draftId) markDraftUsed(form.draftId);
            setForm(empty);
            setSelected(allUsable(channels));
            setShowPreview(false);
        } catch (err) {
            setError(apiError(err.response?.data?.detail) || "Could not publish");
            setShowPreview(false);
        } finally {
            setBusy(false);
        }
    };

    const openPreview = (e) => {
        e.preventDefault();
        setError("");
        if (!form.title.trim()) {
            setError("Add a title before previewing.");
            return;
        }
        setShowPreview(true);
    };

    const startOver = () => {
        setForm(empty);
        setShowPreview(false);
        setError("");
        toast.success("Cleared — start fresh");
    };

    const grammarCheck = async () => {
        setChecking(true);
        try {
            const { data } = await api.post("/admin/ai/grammar-check", {
                title: form.title,
                body_html: form.body_html,
            });
            setForm((f) => ({ ...f, title: data.title || f.title, body_html: data.body_html || f.body_html }));
            toast.success("Spelling & grammar cleaned up");
        } catch (err) {
            toast.error(apiError(err.response?.data?.detail) || "Could not check right now");
        } finally {
            setChecking(false);
        }
    };

    return (
        <div data-testid="admin-compose" className="max-w-4xl">
            <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/15 ring-1 ring-blue-500/30">
                    <Sparkles className="h-4 w-4 text-blue-400" />
                </span>
                <div>
                    <h1 className="font-serif text-3xl text-blue-50">Create Post</h1>
                    <p className="mt-1 text-sm text-slate-300/55">
                        Write it once — it goes to your blog and every connected network.
                    </p>
                </div>
            </div>

            {!form.title && !form.body_html && (
                <ReadyDrafts
                    onUse={(d) => {
                        setForm({
                            ...empty,
                            title: d.title,
                            cover_image_url: d.cover_image_url,
                            excerpt: d.excerpt,
                            caption: d.caption,
                            body_html: d.body_html,
                            draftId: d.id,
                        });
                        toast.success("Draft loaded. Review it, then publish");
                    }}
                />
            )}

            <form onSubmit={openPreview} className="mt-8 space-y-6">
                <div className="rounded-2xl border border-blue-500/15 bg-[#0c172e] p-6">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                Title
                            </label>
                            <input
                                data-testid="compose-title-input"
                                required
                                value={form.title}
                                onChange={set("title")}
                                placeholder="What's the news?"
                                className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                            />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                Date
                            </label>
                            <input
                                data-testid="compose-date-input"
                                type="date"
                                required
                                value={form.date}
                                onChange={set("date")}
                                className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                            />
                        </div>
                    </div>

                    <div className="mt-5">
                        <ImageUpload
                            label="Image (used on the blog and social posts)"
                            value={form.cover_image_url}
                            onChange={(url) => setForm({ ...form, cover_image_url: url })}
                        />
                    </div>

                    <div className="mt-5">
                        <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                            Social caption
                        </label>
                        <textarea
                            data-testid="compose-caption-input"
                            value={form.caption}
                            onChange={set("caption")}
                            rows={3}
                            placeholder="Short text for social networks. A link back to the full post is added automatically."
                            className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                        />
                    </div>

                    <div className="mt-5">
                        <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                            Full post (blog)
                        </label>
                        <RichTextEditor
                            value={form.body_html}
                            onChange={(html) => setForm((f) => ({ ...f, body_html: html }))}
                        />
                    </div>
                </div>

                <div className="rounded-2xl border border-blue-500/15 bg-[#0c172e] p-6">
                    <div className="flex items-center justify-between">
                        <h2 className="font-serif text-lg text-slate-300">Publish to</h2>
                        <Link
                            to="/admin/channels"
                            className="text-xs text-sky-400 hover:underline"
                        >
                            Manage channels
                        </Link>
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {channels.map((c) => {
                            const Icon = platformIcon(c.key);
                            const accent = PLATFORM_ACCENTS[c.key] || "text-sky-400";
                            const usable = c.status === "ready" || c.connected;
                            const on = selected.includes(c.key);
                            return (
                                <button
                                    type="button"
                                    key={c.key}
                                    data-testid={`compose-channel-${c.key}`}
                                    onClick={() => usable && toggle(c.key)}
                                    disabled={!usable}
                                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                                        on
                                            ? "border-blue-400 bg-blue-500/10"
                                            : "border-blue-500/15 bg-[#070e1c]"
                                    } ${usable ? "hover:border-blue-400/60" : "cursor-not-allowed opacity-45"}`}
                                >
                                    <Icon className={`h-5 w-5 ${accent}`} />
                                    <span className="flex-1">
                                        <span className="block text-sm text-slate-300">
                                            {c.name}
                                        </span>
                                        {!usable && (
                                            <span className="block text-[10px] uppercase tracking-wide text-slate-300/40">
                                                Connect first
                                            </span>
                                        )}
                                    </span>
                                    {on && usable && (
                                        <CheckCircle2 className="h-4 w-4 text-blue-400" />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <label className="mt-5 flex items-center gap-2 text-sm text-slate-300/75">
                        <input
                            data-testid="compose-published-toggle"
                            type="checkbox"
                            checked={form.published}
                            onChange={(e) => setForm({ ...form, published: e.target.checked })}
                            className="h-4 w-4 accent-blue-500"
                        />
                        Publish the blog post live (uncheck to save as draft)
                    </label>

                    {error && (
                        <p className="mt-4 flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="h-4 w-4" /> {error}
                        </p>
                    )}

                    <div className="mt-6 flex flex-wrap justify-end gap-3">
                        <button
                            data-testid="compose-preview-button"
                            type="submit"
                            disabled={busy}
                            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-7 py-3 text-sm font-semibold text-[#ffffff] hover:brightness-110 disabled:opacity-60"
                        >
                            <Eye className="h-4 w-4" />
                            Preview
                        </button>
                    </div>
                </div>
            </form>

            {results && (
                <div
                    data-testid="compose-results"
                    className="mt-6 rounded-2xl border border-blue-500/15 bg-[#0c172e] p-6"
                >
                    <div className="flex items-center gap-3">
                        {results.some((r) => r.ok && r.status !== "draft") && (
                            <img
                                src="/images/sprite-neuro.png"
                                alt="Professor Neuro cheering"
                                className="neuro-pop h-9 w-9 rounded-lg object-cover ring-1 ring-emerald-400/40"
                            />
                        )}
                        <h2 className="font-serif text-lg text-slate-300">
                            {results.some((r) => r.ok && r.status !== "draft")
                                ? "Nice — you're live!"
                                : "Results"}
                        </h2>
                    </div>
                    <div className="mt-4 space-y-2">
                        {results.map((r, i) => {
                            const Icon = platformIcon(r.platform);
                            const label = r.page_name
                                ? `${r.platform.replace("_", " ")} · ${r.page_name}`
                                : r.platform.replace("_", " ");
                            return (
                                <div
                                    key={`${r.platform}-${i}`}
                                    className="flex items-center gap-3 rounded-xl border border-blue-500/10 bg-[#070e1c] p-3"
                                >
                                    <Icon className="h-4 w-4 shrink-0 text-slate-400/70" />
                                    <span className="flex-1 text-sm capitalize text-slate-300">
                                        {label}
                                    </span>
                                    {r.ok && r.status === "draft" ? (
                                        <span className="flex items-center gap-1.5 text-xs text-sky-400/80">
                                            <Clock className="h-4 w-4" /> Saved as draft
                                        </span>
                                    ) : r.ok ? (
                                        <span className="flex items-center gap-2 text-xs text-emerald-300">
                                            <span className="flex items-center gap-1.5">
                                                <CheckCircle2 className="h-4 w-4" /> Published
                                            </span>
                                            {r.url && (
                                                <a
                                                    href={r.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`result-view-${r.platform}-${i}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-blue-400/40 px-2.5 py-1 font-semibold text-sky-200 hover:bg-blue-500/10"
                                                >
                                                    {r.platform === "facebook"
                                                        ? "View on Facebook"
                                                        : r.platform === "instagram"
                                                        ? "View on Instagram"
                                                        : "View"}
                                                    <ExternalLink className="h-3 w-3" />
                                                </a>
                                            )}
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1.5 text-xs text-sky-400/80">
                                            <Clock className="h-4 w-4" />{" "}
                                            {r.message || "Pending"}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {showPreview && (
                <div
                    data-testid="compose-preview-modal"
                    className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:p-8"
                >
                    <div className="relative w-full max-w-3xl rounded-2xl border border-blue-500/20 bg-[#0c172e] shadow-2xl">
                        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-2xl border-b border-blue-500/15 bg-[#0c172e]/95 px-6 py-4 backdrop-blur">
                            <div className="flex items-center gap-2">
                                <Eye className="h-4 w-4 text-blue-400" />
                                <h2 className="font-serif text-lg text-slate-200">Preview</h2>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    data-testid="preview-grammar-button"
                                    onClick={grammarCheck}
                                    disabled={checking}
                                    className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 px-4 py-2 text-xs font-semibold text-sky-200 hover:bg-blue-500/10 disabled:opacity-60"
                                >
                                    {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <SpellCheck className="h-4 w-4" />}
                                    {checking ? "Checking…" : "Spelling & grammar check"}
                                </button>
                                <button
                                    data-testid="preview-close-button"
                                    onClick={() => setShowPreview(false)}
                                    className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-white/5 hover:text-slate-200"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto px-6 py-6">
                            <article className="mx-auto max-w-2xl rounded-xl bg-[#fdf8f2] px-7 py-8 text-[#2b2118] shadow-inner">
                                {form.cover_image_url && (
                                    <img
                                        src={form.cover_image_url}
                                        alt={form.title}
                                        className="mb-6 h-56 w-full rounded-lg object-cover"
                                    />
                                )}
                                <p className="text-xs uppercase tracking-[0.15em] text-[#a8896a]">
                                    {form.date}
                                </p>
                                <h1 data-testid="preview-title" className="mt-2 font-serif text-3xl leading-tight text-[#2b2118]">
                                    {form.title || "Untitled post"}
                                </h1>
                                {form.excerpt && (
                                    <p className="mt-3 text-base italic text-[#6b5844]">{form.excerpt}</p>
                                )}
                                <div
                                    data-testid="preview-body"
                                    className="prose-editor mt-6 max-w-none text-[15px] leading-relaxed text-[#3a3026]"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.body_html || "<p>(No content yet)</p>", { ADD_ATTR: ["target"] }) }}
                                />
                            </article>
                        </div>

                        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 rounded-b-2xl border-t border-blue-500/15 bg-[#0c172e]/95 px-6 py-4 backdrop-blur">
                            <button
                                data-testid="preview-startover-button"
                                onClick={startOver}
                                className="inline-flex items-center gap-2 rounded-full border border-red-400/30 px-5 py-2.5 text-sm text-red-300/90 hover:bg-red-500/10"
                            >
                                <RotateCcw className="h-4 w-4" /> Start over
                            </button>
                            <button
                                data-testid="preview-edit-button"
                                onClick={() => setShowPreview(false)}
                                className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 px-5 py-2.5 text-sm text-sky-200 hover:bg-blue-500/10"
                            >
                                <Pencil className="h-4 w-4" /> Edit
                            </button>
                            <button
                                data-testid="preview-approve-button"
                                onClick={doPublish}
                                disabled={busy}
                                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-emerald-400 via-emerald-500 to-emerald-700 px-6 py-2.5 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-60"
                            >
                                <Send className="h-4 w-4" />
                                {busy ? "Publishing…" : "Approve & publish"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
